import { computed, watch } from 'vue';

import { useStorage } from '@vueuse/core';
import { useTheme } from 'vuetify';

import { ThemeName } from '@/types/theme';

const THEME_STORAGE_KEY = 'app-theme';

// Plain function that run before the app is mounted
export function getStoredTheme(): ThemeName {
  const stored = localStorage.getItem(THEME_STORAGE_KEY);
  return (Object.values(ThemeName) as string[]).includes(stored ?? '')
    ? (stored as ThemeName)
    : ThemeName.light;
}

export function useAppTheme() {
  const storedTheme = useStorage<ThemeName>(THEME_STORAGE_KEY, ThemeName.light);

  const isDarkMode = computed<boolean>({
    get: () => storedTheme.value === ThemeName.dark,
    set: (isDark: boolean) => {
      storedTheme.value = isDark ? ThemeName.dark : ThemeName.light;
    }
  });

  const themeName = computed<ThemeName>(() => storedTheme.value);

  return {
    isDarkMode,
    themeName
  };
}

// Call once, from App.vue: applies the stored theme to Vuetify and the document root
export function useAppThemeSync() {
  const vTheme = useTheme();
  const { themeName } = useAppTheme();

  watch(
    themeName,
    (theme: ThemeName) => {
      vTheme.change(theme);

      // Replace the theme class index.html set at load, which would otherwise win over :root
      document.documentElement.classList.remove(
        ...Object.values(ThemeName).map((name) => `v-theme--${name}`)
      );
      document.documentElement.classList.add(`v-theme--${theme}`);

      // Update the document root for scrollbars and CSS variables
      document.documentElement.setAttribute('data-theme', theme);

      // Explicitly tell the browser the color scheme
      document.documentElement.style.colorScheme = theme;
    },
    { immediate: true }
  );
}
